// CMS TC Kimlik Audit — Trust layer reporting for Turkey bookings.
import React, { useState, useEffect, useCallback } from 'react';
import { useCMS } from './CMSContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Search, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const CMSTCKimlikAudit = () => {
  const { token } = useCMS();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (q.trim()) params.set('q', q.trim());
      const res = await fetch(`${API}/cms/tc-kimlik-audit?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Failed to load TC Kimlik audit');
    } finally {
      setLoading(false);
    }
  }, [token, page, limit, q]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchData(); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/cms/tc-kimlik-audit/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tc_kimlik_audit_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6" data-testid="cms-tc-kimlik-audit">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-amber-400" /> TC Kimlik Audit
          </h1>
          <p className="text-slate-400">Bookings where a TC Kimlik number was captured at checkout</p>
        </div>
        <Button onClick={handleExport} disabled={exporting || total === 0} data-testid="tc-audit-export">
          {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Export CSV
        </Button>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 max-w-xl">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search booking ID, email, name, hotel, TC number..."
            className="pl-9 bg-slate-800 border-slate-700 text-white"
            data-testid="tc-audit-search"
          />
        </div>
        <Button type="submit" variant="outline">Search</Button>
      </form>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-slate-400">
              <ShieldCheck className="h-10 w-10 mx-auto mb-3 text-slate-600" />
              No TC Kimlik audit records yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900 text-slate-400 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3 text-left">Booking</th>
                    <th className="px-4 py-3 text-left">Guest</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Country</th>
                    <th className="px-4 py-3 text-left">Hotel</th>
                    <th className="px-4 py-3 text-left">Stay</th>
                    <th className="px-4 py-3 text-left">TC Kimlik</th>
                    <th className="px-4 py-3 text-left">Captured</th>
                    <th className="px-4 py-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {items.map((it) => (
                    <tr key={it.booking_id} className="hover:bg-slate-700/30">
                      <td className="px-4 py-3 text-white font-mono text-xs">{it.booking_id}</td>
                      <td className="px-4 py-3 text-slate-200">{it.guest_first_name} {it.guest_last_name}</td>
                      <td className="px-4 py-3 text-slate-300">{it.guest_email}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-amber-400 border-amber-500/40">{it.customer_country}</Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-300 max-w-[200px] truncate" title={it.hotel_name}>{it.hotel_name}</td>
                      <td className="px-4 py-3 text-slate-400 text-xs">{it.check_in} → {it.check_out}</td>
                      <td className="px-4 py-3 font-mono text-white">{it.tc_kimlik_number}</td>
                      <td className="px-4 py-3 text-slate-400 text-xs">{formatDate(it.tc_kimlik_provided_at || it.created_at)}</td>
                      <td className="px-4 py-3">
                        <Badge className={
                          it.status === 'confirmed' ? 'bg-green-600' :
                          it.status === 'pending_payment' ? 'bg-yellow-600' :
                          it.status === 'cancelled' ? 'bg-red-600' : 'bg-slate-600'
                        }>{it.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-400">
            Showing {items.length} of {total} records
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <span className="flex items-center px-3 text-slate-300 text-sm">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CMSTCKimlikAudit;
